import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import {
    Alert,
    Button,
    CircularProgress,
    IconButton,
    Snackbar,
    Tooltip,
} from '@mui/material';
import MicrosoftExcelIcon from '../icons/MicrosoftExcelIcon';
import {
    fetchWatchlistM365Status,
    syncWatchlistM365,
    WatchlistM365ApiError,
} from '../../utils/watchlistM365Api';
import {
    scopeSecurityOperationEventDetail,
    USER_SETTINGS_OPEN_EVENT,
    WATCHLIST_M365_CONNECTION_CHANGED_EVENT,
    WATCHLIST_HISTORY_OPEN_EVENT,
    WATCHLIST_REVISION_PROGRESS_EVENT,
} from '../../utils/securityResolutionEvents';
import { normalizeWatchlistLifecycle } from '../../utils/watchlistLifecycle';
import { watchlistColumnReview } from '../../utils/importColumnReview';
import useImportColumnConfirmation from '../../hooks/useImportColumnConfirmation';
import { useSecurityOperationContext } from '../../context/SecurityOperationContext';
import { appBarActionButtonSx } from '../../styles/msiAppBarStyles';
import {
    hasRowsToFix,
    notConsideredRows,
    restoredLeadingZerosNote,
    SkippedRowsDialog,
} from './SkippedUploadRows';

const SOURCE_ATTENTION_CODES = new Set([
    'sharing_url_invalid',
    'source_unavailable',
    'source_forbidden',
    'source_too_large',
    'workbook_invalid',
    'workbook_unsupported',
    'worksheet_reselection_required',
    'worksheet_invalid',
]);

const CONNECTION_ATTENTION_CODES = new Set([
    'connection_required',
    'connection_changed',
    'reconnect_required',
    'account_mismatch',
    'permission_missing',
]);

const EXISTING_TERMINAL_OUTCOMES = new Set([
    'failed',
    'cancelled',
    'stale',
    'superseded',
    'succeeded',
    'unchanged',
]);

const CLOSED_SKIPPED_ROWS = Object.freeze({
    open: false,
    title: '',
    lead: '',
    inputValidation: null,
});

const EMPTY_CONNECTION = Object.freeze({
    connected: false,
    status: 'disconnected',
    generation: null,
    workbook_display_name: null,
    worksheet_name: null,
});

function boundedDisplayLabel(value, limit) {
    if (typeof value !== 'string') return null;
    const normalized = value.trim().replace(/\s+/g, ' ');
    return normalized ? normalized.slice(0, limit) : null;
}

function normalizeConnection(value) {
    const connection = value && typeof value === 'object' ? value : {};
    return {
        connected: connection.connected === true,
        status: typeof connection.status === 'string' ? connection.status.trim() : '',
        generation: Number.isInteger(connection.generation) && connection.generation > 0
            ? connection.generation
            : null,
        workbook_display_name: boundedDisplayLabel(connection.workbook_display_name, 160),
        worksheet_name: boundedDisplayLabel(connection.worksheet_name, 128),
    };
}

function safeConnectionLabel(connection) {
    const workbook = connection?.workbook_display_name || '';
    const worksheet = connection?.worksheet_name || '';
    if (!workbook || !worksheet) return '';
    return `${workbook} — ${worksheet}`;
}

function activeConnectionGeneration(connection) {
    return (
        connection?.connected === true
        && connection?.status === 'active'
        && Number.isInteger(connection?.generation)
        && connection.generation > 0
        && safeConnectionLabel(connection)
    ) ? connection.generation : null;
}

// The column check leaves out columns with no header and no values, so only
// blank rows are left to report.
function inputCleanupMessage(inputValidation) {
    const blankRows = Number(inputValidation?.blank_row_count_removed) || 0;
    const restored = restoredLeadingZerosNote(inputValidation);
    return (blankRows > 0
        ? ` Import cleanup: ${blankRows} fully blank row${blankRows === 1 ? '' : 's'} ignored.`
        : '') + (restored ? ` ${restored}` : '');
}

function revisionLocator(response) {
    const revisionId = String(
        response?.watchlist_revision_id || response?.revision_id || '',
    ).trim();
    const revisionSeq = response?.revision_seq;
    return revisionId && Number.isInteger(revisionSeq) && revisionSeq > 0 ? {
        revisionId,
        revisionSeq,
    } : null;
}

function validLifecycle(response, acceptedStatuses, locator) {
    try {
        const lifecycle = normalizeWatchlistLifecycle(response?.lifecycle);
        return (
            acceptedStatuses.has(lifecycle.status)
            && lifecycle.watchlist_revision_id === locator?.revisionId?.toLowerCase()
            && lifecycle.revision_seq === locator?.revisionSeq
        ) ? lifecycle : null;
    } catch (_error) {
        return null;
    }
}

function normalizeSyncOutcome(response) {
    const status = String(response?.status || '').trim();
    const locator = revisionLocator(response);
    if (
        status === 'confirm_columns'
        && response?.changed === false
        && response?.history_written === false
        && !locator
    ) {
        const columnReview = watchlistColumnReview(response?.column_review);
        if (columnReview) {
            return { status, locator: null, lifecycle: null, pendingRevision: null, columnReview };
        }
    }
    if (
        status === 'up_to_date'
        && response?.changed === false
        && response?.history_written === false
        && !locator
    ) {
        return { status, locator: null, lifecycle: null, pendingRevision: null };
    }
    if (
        status === 'source_version_previously_discarded'
        && response?.changed === false
        && response?.history_written === false
        && locator
    ) {
        return { status, locator, lifecycle: null, pendingRevision: null };
    }
    if (status === 'changes_awaiting_review') {
        const pendingRevision = response?.pending_revision;
        const pendingId = String(pendingRevision?.id || '').trim();
        const lifecycle = validLifecycle(
            pendingRevision,
            new Set(['pending_review']),
            locator,
        );
        const responseLifecycle = validLifecycle(
            response,
            new Set(['pending_review']),
            locator,
        );
        if (
            response?.changed === true
            && response?.history_written === true
            && locator
            && pendingId === locator.revisionId
            && lifecycle
            && responseLifecycle
            && pendingRevision?.source_metadata_json?.ingress_kind === 'm365_excel'
        ) {
            return { status, locator, lifecycle, pendingRevision };
        }
    }
    if (status === 'review_in_progress') {
        const lifecycle = validLifecycle(response, new Set(['pending_review']), locator);
        if (
            response?.changed === false
            && response?.history_written === false
            && locator
            && lifecycle
        ) {
            return { status, locator, lifecycle, pendingRevision: null };
        }
    }
    if (status === 'watchlist_operation_in_progress') {
        const lifecycle = validLifecycle(response, new Set([
            'queued',
            'running',
            'failed',
            'cancelled',
        ]), locator);
        if (
            response?.changed === false
            && response?.history_written === false
            && locator
            && lifecycle
        ) {
            return { status, locator, lifecycle, pendingRevision: null };
        }
    }
    if (EXISTING_TERMINAL_OUTCOMES.has(status)) {
        const lifecycle = validLifecycle(response, new Set([status]), locator);
        if (
            response?.changed === false
            && response?.history_written === false
            && locator
            && lifecycle
        ) {
            return { status, locator, lifecycle, pendingRevision: null };
        }
    }
    throw new WatchlistM365ApiError('connection_unavailable');
}

function dispatchSettingsOpen(sessionScopeToken) {
    if (typeof window === 'undefined') return;
    const detail = scopeSecurityOperationEventDetail(
        { tab: 'integrations' },
        sessionScopeToken,
    );
    if (detail) {
        window.dispatchEvent(new CustomEvent(USER_SETTINGS_OPEN_EVENT, { detail }));
    }
}

function dispatchExistingOperation(outcome, sessionScopeToken) {
    if (typeof window === 'undefined' || !outcome?.locator) return;
    const progressDetail = scopeSecurityOperationEventDetail({
        lifecycle: outcome.lifecycle,
        watchlist_revision_id: outcome.locator.revisionId,
        revision_seq: outcome.locator.revisionSeq,
        reason: 'watchlist_m365_sync_existing_operation',
    }, sessionScopeToken);
    if (progressDetail) {
        window.dispatchEvent(new CustomEvent(WATCHLIST_REVISION_PROGRESS_EVENT, {
            detail: progressDetail,
        }));
    }
    const historyDetail = scopeSecurityOperationEventDetail({
        revisionId: outcome.locator.revisionId,
        revisionSeq: outcome.locator.revisionSeq,
    }, sessionScopeToken);
    if (historyDetail) {
        window.dispatchEvent(new CustomEvent(WATCHLIST_HISTORY_OPEN_EVENT, {
            detail: historyDetail,
        }));
    }
}

function newSyncRequestId() {
    if (typeof crypto === 'undefined' || typeof crypto.randomUUID !== 'function') {
        throw new WatchlistM365ApiError('request_invalid');
    }
    return crypto.randomUUID();
}

export default function WatchlistM365SyncControl({ onReviewHandoff }) {
    const { sessionScopeToken } = useSecurityOperationContext();
    const [capability, setCapability] = useState(null);
    const [connection, setConnection] = useState(EMPTY_CONNECTION);
    const [loading, setLoading] = useState(true);
    const [syncing, setSyncing] = useState(false);
    const [attentionCode, setAttentionCode] = useState('');
    const [feedback, setFeedback] = useState({ open: false, message: '', severity: 'info' });
    const [skippedRows, setSkippedRows] = useState(CLOSED_SKIPPED_ROWS);
    const mountedRef = useRef(false);
    const epochRef = useRef(0);
    const activeRequestRef = useRef(false);
    const retryIntentRef = useRef(null);
    const controllersRef = useRef(new Set());
    // Every Sync shows the worksheet's columns for the user to confirm.
    const {
        confirmColumns,
        columnDialog,
        cancelColumnConfirmation,
    } = useImportColumnConfirmation('watchlist');

    const isCurrent = useCallback((epoch) => (
        mountedRef.current && epochRef.current === epoch
    ), []);

    useEffect(() => {
        const controllers = controllersRef.current;
        mountedRef.current = true;
        epochRef.current += 1;
        const epoch = epochRef.current;
        activeRequestRef.current = false;
        retryIntentRef.current = null;
        controllers.forEach((controller) => controller.abort());
        controllers.clear();
        setCapability(null);
        setConnection(EMPTY_CONNECTION);
        setLoading(true);
        setSyncing(false);
        setAttentionCode('');
        setFeedback({ open: false, message: '', severity: 'info' });
        setSkippedRows(CLOSED_SKIPPED_ROWS);
        const loadStatus = (requestEpoch) => {
            const controller = new AbortController();
            controllers.add(controller);
            setLoading(true);
            fetchWatchlistM365Status({ signal: controller.signal })
                .then((response) => {
                    if (!isCurrent(requestEpoch)) return;
                    setCapability({
                        enabled: response?.enabled === true,
                        configured: response?.configured === true,
                    });
                    setConnection(normalizeConnection(response?.connection));
                    setAttentionCode('');
                })
                .catch((error) => {
                    if (!isCurrent(requestEpoch) || error?.name === 'AbortError') return;
                    setCapability(null);
                    setConnection(EMPTY_CONNECTION);
                })
                .finally(() => {
                    controllers.delete(controller);
                    if (isCurrent(requestEpoch)) setLoading(false);
                });
        };
        const handleConnectionChanged = () => {
            if (activeRequestRef.current) return;
            cancelColumnConfirmation();
            epochRef.current += 1;
            const refreshEpoch = epochRef.current;
            retryIntentRef.current = null;
            controllers.forEach((controller) => controller.abort());
            controllers.clear();
            loadStatus(refreshEpoch);
        };
        loadStatus(epoch);
        window.addEventListener(
            WATCHLIST_M365_CONNECTION_CHANGED_EVENT,
            handleConnectionChanged,
        );
        return () => {
            mountedRef.current = false;
            epochRef.current += 1;
            activeRequestRef.current = false;
            retryIntentRef.current = null;
            controllers.forEach((activeController) => activeController.abort());
            controllers.clear();
            window.removeEventListener(
                WATCHLIST_M365_CONNECTION_CHANGED_EVENT,
                handleConnectionChanged,
            );
        };
    }, [cancelColumnConfirmation, isCurrent, sessionScopeToken]);

    const openSettings = useCallback(() => {
        retryIntentRef.current = null;
        dispatchSettingsOpen(sessionScopeToken);
    }, [sessionScopeToken]);

    const handleSync = useCallback(() => {
        const generation = activeConnectionGeneration(connection);
        if (
            activeRequestRef.current
            || syncing
            || generation == null
        ) return;

        let intent = retryIntentRef.current;
        if (!intent || intent.generation !== generation) {
            try {
                intent = { requestId: newSyncRequestId(), generation };
            } catch (error) {
                setFeedback({ open: true, message: error.message, severity: 'error' });
                return;
            }
            retryIntentRef.current = intent;
        }

        activeRequestRef.current = true;
        const epoch = epochRef.current;
        const controller = new AbortController();
        controllersRef.current.add(controller);
        setSyncing(true);
        setAttentionCode('');
        setFeedback({ open: false, message: '', severity: 'info' });

        // A Sync first answers with the worksheet's column check. The user's
        // choice goes back with the same request id; a worksheet that changed
        // meanwhile is checked again, and Cancel ends the Sync with nothing staged.
        const syncColumns = (columnSelection) => syncWatchlistM365(
            intent.requestId,
            generation,
            columnSelection,
            { signal: controller.signal },
        )
            .then(async (response) => {
                if (!isCurrent(epoch)) return;
                const outcome = normalizeSyncOutcome(response);
                if (outcome.status === 'confirm_columns') {
                    // No request is open while the user chooses.
                    activeRequestRef.current = false;
                    setSyncing(false);
                    const chosen = await confirmColumns({
                        review: outcome.columnReview,
                        sourceLabel: `Linked worksheet ${safeConnectionLabel(connection)}`,
                    });
                    if (!isCurrent(epoch)) return;
                    if (!chosen) {
                        retryIntentRef.current = null;
                        return;
                    }
                    activeRequestRef.current = true;
                    setSyncing(true);
                    await syncColumns(chosen);
                    return;
                }
                const cleanupMessage = inputCleanupMessage(response?.input_validation);
                retryIntentRef.current = null;
                if (outcome.status === 'up_to_date') {
                    const upToDate = 'The Watchlist already matches the linked worksheet. Nothing to review.';
                    // No review opens, so name the skipped rows that need a fix here.
                    if (hasRowsToFix(response?.input_validation)) {
                        setSkippedRows({
                            open: true,
                            title: 'Some rows were skipped',
                            lead: upToDate,
                            inputValidation: response.input_validation,
                        });
                        return;
                    }
                    setFeedback({
                        open: true,
                        message: `${upToDate}${cleanupMessage}`,
                        severity: 'info',
                    });
                    return;
                }
                if (outcome.status === 'source_version_previously_discarded') {
                    setFeedback({
                        open: true,
                        message: 'This linked worksheet version was already discarded. Nothing new is awaiting review.',
                        severity: 'info',
                    });
                    return;
                }
                if (
                    outcome.status === 'changes_awaiting_review'
                    || outcome.status === 'review_in_progress'
                ) {
                    if (outcome.status === 'changes_awaiting_review') {
                        onReviewHandoff({
                            status: outcome.status,
                            revisionId: outcome.locator.revisionId,
                            revisionSeq: outcome.locator.revisionSeq,
                            pendingRevision: outcome.pendingRevision,
                            sessionScopeToken,
                        });
                    }
                    const historyDetail = scopeSecurityOperationEventDetail({
                        revisionId: outcome.locator.revisionId,
                        revisionSeq: outcome.locator.revisionSeq,
                    }, sessionScopeToken);
                    if (historyDetail) {
                        window.dispatchEvent(new CustomEvent(WATCHLIST_HISTORY_OPEN_EVENT, {
                            detail: historyDetail,
                        }));
                    }
                    setFeedback({
                        open: true,
                        message: outcome.status === 'changes_awaiting_review'
                            ? `Changes from Excel are awaiting review. Apply is still required to update your Watchlist.${cleanupMessage}`
                            : `A Watchlist change is already awaiting review.${cleanupMessage}`,
                        severity: 'info',
                    });
                    return;
                }
                dispatchExistingOperation(outcome, sessionScopeToken);
                setFeedback({
                    open: true,
                    message: outcome.status === 'watchlist_operation_in_progress'
                        ? 'A Watchlist update is already in progress. Its existing status is open.'
                        : 'This Sync result is already recorded. Its existing status is open.',
                    severity: 'info',
                });
            });

        syncColumns(null)
            .catch((error) => {
                if (!isCurrent(epoch) || error?.name === 'AbortError') return;
                const safeError = error instanceof WatchlistM365ApiError
                    ? error
                    : new WatchlistM365ApiError('connection_unavailable', { retryable: true });
                if (!safeError.retryable) retryIntentRef.current = null;
                if (
                    CONNECTION_ATTENTION_CODES.has(safeError.code)
                    || SOURCE_ATTENTION_CODES.has(safeError.code)
                ) {
                    setAttentionCode(safeError.code);
                    dispatchSettingsOpen(sessionScopeToken);
                }
                // Every row was skipped: name them, as a rejected file upload does.
                if (notConsideredRows(safeError.inputValidation).rows.length > 0) {
                    setSkippedRows({
                        open: true,
                        title: 'Linked worksheet could not be processed',
                        lead: '',
                        inputValidation: safeError.inputValidation,
                    });
                    return;
                }
                setFeedback({
                    open: true,
                    message: safeError.retryable
                        ? `${safeError.message} Try Sync from Excel again.`
                        : safeError.message,
                    severity: 'error',
                });
            })
            .finally(() => {
                controllersRef.current.delete(controller);
                if (isCurrent(epoch)) {
                    activeRequestRef.current = false;
                    setSyncing(false);
                }
            });
    }, [confirmColumns, connection, isCurrent, onReviewHandoff, sessionScopeToken, syncing]);

    if (loading || !capability || !capability.enabled || !capability.configured) {
        return null;
    }

    const connectionActive = activeConnectionGeneration(connection) != null;
    const needsReconnect = connection?.status === 'reconnect_required';
    const attention = Boolean(attentionCode);
    const needsConfiguration = !connectionActive && !needsReconnect && !attention;
    const label = syncing
        ? 'Checking Excel…'
        : attention
            ? 'Excel needs attention'
            : needsReconnect
                ? 'Reconnect Excel'
                : needsConfiguration
                    ? 'Set up Excel'
                    : 'Sync from Excel';
    const tooltip = connectionActive && !attention
        ? `${label}. Check ${safeConnectionLabel(connection) || 'the linked worksheet'} for Watchlist changes. This runs only when you click.`
        : `${label}. Open Integrations Settings to finish the Linked Excel Watchlist connection.`;

    return (
        <>
            <Tooltip title={tooltip} arrow>
                <span>
                    <IconButton
                        size="small"
                        onClick={connectionActive && !attention ? handleSync : openSettings}
                        disabled={syncing}
                        aria-label={label}
                        sx={appBarActionButtonSx}
                    >
                        {syncing
                            ? <CircularProgress size={14} thickness={5} color="inherit" />
                            : attention || needsReconnect || needsConfiguration
                                ? (
                                    <MicrosoftExcelIcon
                                        sx={{
                                            fontSize: 20,
                                            filter: 'grayscale(0.15)',
                                            opacity: 0.86,
                                        }}
                                    />
                                )
                                : <MicrosoftExcelIcon sx={{ fontSize: 20 }} />}
                    </IconButton>
                </span>
            </Tooltip>
            {columnDialog}
            <SkippedRowsDialog
                open={skippedRows.open}
                title={skippedRows.title}
                lead={skippedRows.lead}
                inputValidation={skippedRows.inputValidation}
                onClose={() => setSkippedRows((dialog) => ({ ...dialog, open: false }))}
            />
            <Snackbar
                open={feedback.open}
                autoHideDuration={8000}
                onClose={(_, reason) => {
                    if (reason !== 'clickaway') {
                        setFeedback((current) => ({ ...current, open: false }));
                    }
                }}
            >
                <Alert
                    severity={feedback.severity}
                    action={attention ? (
                        <Button color="inherit" size="small" onClick={openSettings}>
                            Open Settings
                        </Button>
                    ) : null}
                >
                    {feedback.message}
                </Alert>
            </Snackbar>
        </>
    );
}

WatchlistM365SyncControl.propTypes = {
    onReviewHandoff: PropTypes.func.isRequired,
};

export {
    dispatchExistingOperation,
    normalizeSyncOutcome,
};
